import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Save, RefreshCw, Tag } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import DatePicker from 'react-datepicker';

const CreateOffer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [listings, setListings] = useState([]);
  const [selectedListings, setSelectedListings] = useState([]);
  const [listingSearchTerm, setListingSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  
  const [offerData, setOfferData] = useState({
    title: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    coupon_code: '',
    apply_to: 'all',
    category: '',
    subcategory: '',
    min_order_value: 0,
    max_discount: '',
    start_date: new Date(),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    usage_limit: '',
    per_user_limit: 1,
    active: true,
    terms_conditions: '',
    funded_by: 'vendor',
    vendor_funding_percent: 50
  });
  
  useEffect(() => {
    fetchListings();
    fetchCategories();
    if (id) {
      fetchOffer();
    }
  }, [id]);

  // iter-411 — "Select Listings" had no search at all and only ever
  // loaded the newest 200 active listings, so any older product (e.g.
  // reported: "Bhaula Godham A2 cow ghee") could never be found or
  // selected here. Debounced backend search merges in matches beyond
  // that cutoff.
  useEffect(() => {
    if (!listingSearchTerm) return;
    const handle = setTimeout(() => {
      fetchListings(listingSearchTerm);
    }, 400);
    return () => clearTimeout(handle);
  }, [listingSearchTerm]);
  
  const fetchCategories = async () => {
    try {
      const data = await apiCall('/api/categories');
      setCategories(Array.isArray(data) ? data : data?.categories || []);
    } catch { setCategories([]); }
  };
  
  const selectedCategory = categories.find(c => c.id === offerData.category || c.slug === offerData.category || c.name === offerData.category);
  const subcategories = selectedCategory?.subcategories || [];
  
  const fetchListings = async (search = '') => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const data = await apiCall(`/api/listings/?status=active${searchParam}`);
      if (!search) {
        setListings(data);
      } else {
        // Merge-only: a typed search term asks the backend directly so
        // listings older than the newest-200 cutoff (which never load by
        // default) can still be found and selected, without discarding
        // the already-loaded baseline set.
        setListings(prev => {
          const byId = new Map(prev.map(l => [l.id, l]));
          (data || []).forEach(l => byId.set(l.id, l));
          return Array.from(byId.values());
        });
      }
    } catch (error) {
      console.error('Failed to load listings');
    }
  };
  
  const fetchOffer = async () => {
    try {
      const data = await apiCall(`/api/offers/${id}`);
      setOfferData({
        ...data,
        start_date: new Date(data.start_date),
        end_date: new Date(data.end_date)
      });
      setSelectedListings(data.listing_ids || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load offer',
        variant: 'destructive'
      });
      navigate('/offers');
    } finally {
      setLoading(false);
    }
  };
  
  const generateCoupon = async () => {
    try {
      const data = await apiCall('/api/offers/generate-code', { method: 'POST' });
      setOfferData({ ...offerData, coupon_code: data.coupon_code });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to generate coupon',
        variant: 'destructive'
      });
    }
  };
  
  const toggleListing = (listingId) => {
    setSelectedListings(prev =>
      prev.includes(listingId)
        ? prev.filter(id => id !== listingId)
        : [...prev, listingId]
    );
  };
  
  const handleSubmit = async () => {
    if (!offerData.title || !offerData.coupon_code) {
      toast({
        title: 'Error',
        description: 'Please fill in required fields',
        variant: 'destructive'
      });
      return;
    }
    
    if (!offerData.discount_value || parseFloat(offerData.discount_value) <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter valid discount value',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        ...offerData,
        discount_value: parseFloat(offerData.discount_value),
        min_order_value: parseFloat(offerData.min_order_value) || 0,
        max_discount: offerData.max_discount ? parseFloat(offerData.max_discount) : null,
        usage_limit: offerData.usage_limit ? parseInt(offerData.usage_limit) : null,
        per_user_limit: parseInt(offerData.per_user_limit) || 1,
        listing_ids: offerData.apply_to === 'specific' ? selectedListings : [],
        start_date: offerData.start_date.toISOString(),
        end_date: offerData.end_date.toISOString(),
        funded_by: offerData.funded_by || 'vendor',
        vendor_funding_percent: offerData.vendor_funding_percent !== '' && offerData.vendor_funding_percent != null
          ? parseFloat(offerData.vendor_funding_percent) : 50
      };
      
      if (id) {
        await apiCall(`/api/offers/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        toast({
          title: 'Success',
          description: 'Offer updated successfully'
        });
      } else {
        const result = await apiCall('/api/offers/', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        const shopifyMsg = result.shopify_synced 
          ? ' and synced to Shopify' 
          : (result.shopify_error ? ` (Shopify sync: ${result.shopify_error})` : '');
        toast({
          title: 'Success',
          description: `Offer created successfully${shopifyMsg}`
        });
      }
      
      navigate('/offers');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/offers')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Offers
          </Button>
        </div>
        
        <div className="flex items-center gap-3">
          <Tag className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {id ? 'Edit Offer' : 'Create New Offer'}
            </h1>
            <p className="text-gray-600">Set up promotional offers and discounts</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Offer Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Offer Title *</Label>
                  <Input
                    placeholder="e.g., Summer Sale 2025"
                    value={offerData.title}
                    onChange={(e) => setOfferData({ ...offerData, title: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Describe the offer..."
                    value={offerData.description}
                    onChange={(e) => setOfferData({ ...offerData, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Discount Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discount Type *</Label>
                    <Select 
                      value={offerData.discount_type}
                      onValueChange={(v) => setOfferData({ ...offerData, discount_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Discount Value *</Label>
                    <Input
                      type="number"
                      placeholder={offerData.discount_type === 'percentage' ? '10' : '100'}
                      value={offerData.discount_value}
                      onChange={(e) => setOfferData({ ...offerData, discount_value: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Order Value (₹)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={offerData.min_order_value}
                      onChange={(e) => setOfferData({ ...offerData, min_order_value: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Max Discount (₹)</Label>
                    <Input
                      type="number"
                      placeholder="Optional"
                      value={offerData.max_discount}
                      onChange={(e) => setOfferData({ ...offerData, max_discount: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="discount-funding-card">
              <CardHeader>
                <CardTitle>Who Funds This Discount?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isAdmin ? (
                  <>
                    <div className="space-y-2">
                      <Label>Funding Source</Label>
                      <Select
                        value={offerData.funded_by}
                        onValueChange={(v) => setOfferData({
                          ...offerData,
                          funded_by: v,
                          vendor_funding_percent: v === 'vendor' ? 100 : v === 'marketplace' ? 0 : 50
                        })}
                      >
                        <SelectTrigger data-testid="funded-by-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vendor">Vendor — the vendor's payout absorbs the full discount</SelectItem>
                          <SelectItem value="marketplace">Marketplace — Aniyor absorbs the full discount, vendor paid in full</SelectItem>
                          <SelectItem value="both">Both — split the cost by a custom ratio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {offerData.funded_by === 'both' && (
                      <div className="space-y-2">
                        <Label>Vendor's Share of the Discount (%)</Label>
                        <Input
                          data-testid="vendor-funding-percent-input"
                          type="number"
                          min="0"
                          max="100"
                          value={offerData.vendor_funding_percent}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              setOfferData({ ...offerData, vendor_funding_percent: '' });
                              return;
                            }
                            const clamped = Math.min(100, Math.max(0, parseFloat(raw) || 0));
                            setOfferData({ ...offerData, vendor_funding_percent: clamped });
                          }}
                          onBlur={() => {
                            if (offerData.vendor_funding_percent === '') {
                              setOfferData({ ...offerData, vendor_funding_percent: 50 });
                            }
                          }}
                        />
                        <p className="text-sm text-gray-600">
                          Vendor bears {offerData.vendor_funding_percent === '' ? 50 : offerData.vendor_funding_percent}% · Marketplace bears {100 - (offerData.vendor_funding_percent === '' ? 50 : parseFloat(offerData.vendor_funding_percent) || 0)}% of every discount redeemed.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-600" data-testid="vendor-funding-locked-note">
                    As a vendor, offers you create are always <strong>funded by you</strong> — the discount amount is deducted from your own payout. Marketplace-funded or split-cost offers can only be set up by Aniyor admins.
                  </p>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Coupon Code</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter or generate coupon code"
                    value={offerData.coupon_code}
                    onChange={(e) => setOfferData({ ...offerData, coupon_code: e.target.value.toUpperCase() })}
                    className="flex-1"
                  />
                  <Button onClick={generateCoupon} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate
                  </Button>
                </div>
                <p className="text-sm text-gray-600">
                  This code will be used by customers to apply the discount
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Apply To</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Offer Applies To *</Label>
                  <Select 
                    value={offerData.apply_to}
                    onValueChange={(v) => setOfferData({ ...offerData, apply_to: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Listings</SelectItem>
                      <SelectItem value="category">Specific Category</SelectItem>
                      <SelectItem value="specific">Specific Listings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {offerData.apply_to === 'category' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={offerData.category}
                        onValueChange={(value) => setOfferData({ ...offerData, category: value, subcategory: '' })}
                      >
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id || cat.slug} value={cat.id || cat.slug}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subcategory</Label>
                      <Select
                        value={offerData.subcategory}
                        onValueChange={(value) => setOfferData({ ...offerData, subcategory: value })}
                        disabled={!offerData.category || subcategories.length === 0}
                      >
                        <SelectTrigger data-testid="subcategory-select">
                          <SelectValue placeholder={subcategories.length === 0 ? 'Select category first' : 'All subcategories'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Subcategories</SelectItem>
                          {subcategories.map(sub => (
                            <SelectItem key={sub.id || sub.slug} value={sub.id || sub.slug}>
                              {sub.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                
                {offerData.apply_to === 'specific' && (
                  <div className="space-y-2">
                    <Label>Select Listings</Label>
                    <Input
                      data-testid="listing-picker-search-input"
                      placeholder="Search listings by title or category..."
                      value={listingSearchTerm}
                      onChange={(e) => setListingSearchTerm(e.target.value)}
                    />
                    <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                      {listings
                        .filter(listing => {
                          if (!listingSearchTerm) return true;
                          const q = listingSearchTerm.toLowerCase();
                          // Match what the backend search actually covers
                          // (title/category/subcategory) so a backend-found
                          // match doesn't get hidden again by a narrower
                          // client-side filter.
                          return listing.title?.toLowerCase().includes(q) ||
                                 listing.category?.toLowerCase().includes(q) ||
                                 listing.subcategory?.toLowerCase().includes(q);
                        })
                        .map(listing => (
                        <div key={listing.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedListings.includes(listing.id)}
                            onCheckedChange={() => toggleListing(listing.id)}
                          />
                          <span className="text-sm">{listing.title}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600">
                      {selectedListings.length} listing(s) selected
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Validity & Usage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <DatePicker
                      selected={offerData.start_date}
                      onChange={(date) => setOfferData({ ...offerData, start_date: date })}
                      dateFormat="yyyy-MM-dd"
                      className="w-full p-2 border rounded-md"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>End Date *</Label>
                    <DatePicker
                      selected={offerData.end_date}
                      onChange={(date) => setOfferData({ ...offerData, end_date: date })}
                      dateFormat="yyyy-MM-dd"
                      minDate={offerData.start_date}
                      className="w-full p-2 border rounded-md"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Total Usage Limit</Label>
                    <Input
                      type="number"
                      placeholder="Unlimited"
                      value={offerData.usage_limit}
                      onChange={(e) => setOfferData({ ...offerData, usage_limit: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Per User Limit *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={offerData.per_user_limit}
                      onChange={(e) => setOfferData({ ...offerData, per_user_limit: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Terms & Conditions</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter terms and conditions..."
                  value={offerData.terms_conditions}
                  onChange={(e) => setOfferData({ ...offerData, terms_conditions: e.target.value })}
                  rows={5}
                />
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={offerData.active}
                    onCheckedChange={(checked) => setOfferData({ ...offerData, active: checked })}
                  />
                  <Label>Active Offer</Label>
                </div>
                <p className="text-sm text-gray-600">
                  Only active offers can be used by customers
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold mb-2">
                    {offerData.discount_type === 'percentage'
                      ? `${offerData.discount_value}% OFF`
                      : `₹${offerData.discount_value} OFF`
                    }
                  </p>
                  <code className="bg-white text-gray-900 px-3 py-1 rounded font-mono text-sm">
                    {offerData.coupon_code || 'COUPON'}
                  </code>
                </div>
              </CardContent>
            </Card>
            
            <Button 
              className="w-full" 
              onClick={handleSubmit}
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : id ? 'Update Offer' : 'Create Offer'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateOffer;
